import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import OverallDonut from "./OverallDonut";
import { targetLabels, profileTypeLabels } from "../utils/categoryLabels";
import { targetsToProfileTypes, getAllowedTargetTypes } from "../utils/targets";
import { FaSearch, FaUser, FaFolderOpen, FaShieldAlt, FaBug, FaSyncAlt } from "react-icons/fa";
import { AiFillSecurityScan } from "react-icons/ai";
import "../styles/ReportDashboard.css";

/**
 * Overall Readiness section: target selector + donuts and chips per profile type.
 * Props: targetOptions, selectedTargetId, onTargetChange, allStats, loading, onRefresh
 */
function OverallReadinessSection({
  targetOptions = [],
  selectedTargetId,
  onTargetChange,
  allStats = [],
  loading = false,
  onRefresh,
}) {
  const { t } = useTranslation("dashboard");

  const targetTypeOptions = useMemo(() => {
    const types = new Set();
    targetOptions.forEach((o) => {
      if (o.target_type) types.add(o.target_type);
    });
    return Array.from(types);
  }, [targetOptions]);

  const [selectedTargetType, setSelectedTargetType] = useState("");

  const filteredTargetOptions = useMemo(() => {
    if (!selectedTargetType) return [];
    return targetOptions.filter((o) => o.target_type === selectedTargetType);
  }, [targetOptions, selectedTargetType]);

  useEffect(() => {
    if (loading || targetTypeOptions.length === 0) return;
    if (!selectedTargetType || !targetTypeOptions.includes(selectedTargetType)) {
      setSelectedTargetType(targetTypeOptions[0]);
    }
  }, [loading, targetTypeOptions, selectedTargetType]);

  useEffect(() => {
    if (loading || filteredTargetOptions.length === 0) return;
    const currentInList = filteredTargetOptions.some(
      (o) => String(o.target_id) === String(selectedTargetId)
    );
    if (!selectedTargetId || !currentInList) {
      onTargetChange(String(filteredTargetOptions[0].target_id));
    }
  }, [loading, filteredTargetOptions, selectedTargetId, onTargetChange]);

  const selTargetId = selectedTargetId ? String(selectedTargetId).trim() || null : null;
  const selTarget = targetOptions.find((o) => String(o.target_id) === selTargetId);
  const reportsForSel =
    selTargetId != null
      ? allStats.filter((r) => r.target_id != null && String(r.target_id) === selTargetId)
      : [];
  const selTargetType =
    reportsForSel[0]?.target_type ??
    selTarget?.target_type ??
    (reportsForSel[0]?.profile_type
      ? getAllowedTargetTypes(reportsForSel[0].profile_type)[0] ?? null
      : null) ??
    null;
  const profileTypes =
    selTargetType && targetsToProfileTypes[selTargetType]
      ? targetsToProfileTypes[selTargetType]
      : [];

  const byProfileType = {};
  profileTypes.forEach((pt) => {
    byProfileType[pt] = reportsForSel.filter((r) => r.profile_type === pt);
  });

  const avgReadinessByProfileType = {};
  profileTypes.forEach((pt) => {
    const rows = byProfileType[pt] || [];
    if (rows.length === 0) {
      avgReadinessByProfileType[pt] = 0;
    } else {
      const sum = rows.reduce(
        (s, r) => s + (typeof r.overall_readiness === "number" ? r.overall_readiness : 0),
        0
      );
      avgReadinessByProfileType[pt] = Math.round((sum / rows.length) * 10) / 10;
    }
  });

  const testStatsByProfileType = {};
  profileTypes.forEach((pt) => {
    const rows = byProfileType[pt] || [];
    let totalTests = 0;
    let totalFailed = 0;
    let totalCategoriesTested = 0;
    rows.forEach((r) => {
      const qs = r.score_json?.quick_stats;
      totalTests += typeof qs?.total_tests === "number" ? qs.total_tests : 0;
      totalFailed += typeof qs?.total_failed_tests === "number" ? qs.total_failed_tests : 0;
      totalCategoriesTested += typeof qs?.total_categories_tested === "number" ? qs.total_categories_tested : 0;
    });
    testStatsByProfileType[pt] = {
      total_tests: totalTests,
      total_categories_tested: totalCategoriesTested,
      blocked: totalTests - totalFailed,
      failed_to_block: totalFailed,
    };
  });

  const targetTypeLabel = (type) =>
    type ? (targetLabels[type] ?? type) : t("overallReadiness.emptyValue");
  const profileTypeLabel = (pt) => profileTypeLabels[pt] ?? pt;
  const refreshLabel = loading
    ? t("overallReadiness.loadingTargets")
    : t("overallReadiness.refreshTargets");

  return (
    <div className="target-detail-card">
      <div className="target-detail-heading-row">
        <div className="target-detail-heading">{t("overallReadiness.title")}</div>
        {typeof onRefresh === "function" && (
          <button
            type="button"
            className="target-refresh-btn"
            onClick={onRefresh}
            disabled={loading}
            title={refreshLabel}
            aria-label={refreshLabel}
          >
            <FaSyncAlt className={loading ? "target-refresh-spin" : undefined} />
          </button>
        )}
        <span className="target-readiness-disclaimer">
          {t("overallReadiness.disclaimer")}
        </span>
      </div>
      <div className="target-select-row">
        <label htmlFor="target-type-select">{t("overallReadiness.targetType")}</label>
        <select
          id="target-type-select"
          value={selectedTargetType}
          onChange={(e) => {
            const newType = e.target.value;
            setSelectedTargetType(newType);
            const firstOfType = targetOptions.find((o) => o.target_type === newType);
            onTargetChange(firstOfType ? String(firstOfType.target_id) : "");
          }}
          disabled={loading}
        >
          {loading && <option value="">{t("overallReadiness.loadingTargetsOption")}</option>}
          {!loading &&
            targetTypeOptions.map((type) => (
              <option key={type} value={type}>
                {targetTypeLabel(type)}
              </option>
            ))}
        </select>

        <label htmlFor="target-select">{t("overallReadiness.target")}</label>
        <select
          id="target-select"
          value={selectedTargetId ?? ""}
          onChange={(e) => onTargetChange(e.target.value)}
          disabled={loading || !selectedTargetType}
        >
          {loading && <option value="">{t("overallReadiness.loadingTargetsOption")}</option>}
          {!loading && filteredTargetOptions.length === 0 && (
            <option value="">{t("overallReadiness.noTargetsForType")}</option>
          )}
          {!loading &&
            filteredTargetOptions.map((opt) => (
              <option key={opt.target_id} value={opt.target_id}>
                {opt.target_name}
              </option>
            ))}
        </select>
      </div>
      {selTargetId != null && (
        <>
          <div className="target-selected-summary">
            <strong>{t("overallReadiness.targetId")}</strong> {selTargetId}
            <span style={{ marginLeft: "20px" }}>
              <strong>{t("overallReadiness.name")}</strong>{" "}
              {selTarget?.target_name ?? reportsForSel[0]?.target_name ?? t("overallReadiness.emptyValue")}
            </span>
            <span style={{ marginLeft: "20px" }}>
              <strong>{t("overallReadiness.type")}</strong>{" "}
              {targetTypeLabel(selTargetType)}
            </span>
          </div>
          <div className="target-donuts-row">
            {profileTypes.map((pt) => {
              const scansCount = (byProfileType[pt] || []).length;
              const profilesCount = scansCount
                ? new Set((byProfileType[pt] || []).map((r) => r.profile_id)).size
                : 0;
              const testsCount = testStatsByProfileType[pt]?.total_tests ?? 0;
              const categoriesCount = testStatsByProfileType[pt]?.total_categories_tested ?? 0;
              const isEmpty = scansCount === 0;
              const chipStyle = isEmpty
                ? {
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "6px 12px",
                    borderRadius: "999px",
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "#9ca3af",
                    background: "rgba(156, 163, 175, 0.2)",
                    border: "1px solid rgba(156, 163, 175, 0.4)",
                    boxShadow: "none",
                  }
                : null;
              return (
                <div
                  key={pt}
                  className="target-donut-column"
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: "16px",
                    boxSizing: "border-box",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      flex: 1,
                      minWidth: 0,
                      ...(isEmpty ? { opacity: 0.65, filter: "grayscale(1)" } : {}),
                    }}
                  >
                    <OverallDonut title="" value={avgReadinessByProfileType[pt] ?? 0} />
                    <div
                      className="target-profile-type-name"
                      style={isEmpty ? { color: "#9ca3af" } : undefined}
                    >
                      {profileTypeLabel(pt)}
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      flexWrap: "wrap",
                      gap: "8px",
                      flex: 1,
                      minWidth: 0,
                      alignItems: "center",
                    }}
                  >
                    <span
                      style={
                        chipStyle ?? {
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          padding: "6px 12px",
                          borderRadius: "999px",
                          fontSize: "13px",
                          fontWeight: "600",
                          color: "#334155",
                          background: "rgba(255, 255, 255, 0.95)",
                          border: "1px solid rgba(148, 163, 184, 0.5)",
                          boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                        }
                      }
                    >
                      <FaSearch size={12} style={{ color: isEmpty ? "#9ca3af" : "#64748b" }} />{" "}
                      {scansCount}{" "}
                      {scansCount === 1
                        ? t("overallReadiness.scan")
                        : t("overallReadiness.scans")}
                    </span>
                    <span
                      style={
                        chipStyle ?? {
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          padding: "6px 12px",
                          borderRadius: "999px",
                          fontSize: "13px",
                          fontWeight: "600",
                          color: "#475569",
                          background: "rgba(255, 255, 255, 0.95)",
                          border: "1px solid rgba(148, 163, 184, 0.5)",
                          boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                        }
                      }
                    >
                      <FaUser size={12} style={{ color: isEmpty ? "#9ca3af" : "#64748b" }} />{" "}
                      {profilesCount}{" "}
                      {profilesCount === 1
                        ? t("overallReadiness.profile")
                        : t("overallReadiness.profiles")}
                    </span>
                    <span
                      style={
                        chipStyle ?? {
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          padding: "6px 12px",
                          borderRadius: "999px",
                          fontSize: "13px",
                          fontWeight: "600",
                          color: "#a16207",
                          background: "rgba(254, 249, 195, 0.98)",
                          border: "1px solid rgba(202, 138, 4, 0.5)",
                          boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                        }
                      }
                    >
                      <FaFolderOpen
                        size={12}
                        style={{ color: isEmpty ? "#9ca3af" : "#a16207" }}
                      />{" "}
                      {categoriesCount}{" "}
                      {categoriesCount === 1
                        ? t("overallReadiness.category")
                        : t("overallReadiness.categories")}
                    </span>
                    <span
                      style={
                        chipStyle ?? {
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          padding: "6px 12px",
                          borderRadius: "999px",
                          fontSize: "13px",
                          fontWeight: "600",
                          color: "#4f46e5",
                          background: "rgba(238, 242, 255, 0.98)",
                          border: "1px solid rgba(99, 102, 241, 0.4)",
                          boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                        }
                      }
                    >
                      <AiFillSecurityScan
                        size={12}
                        style={{ color: isEmpty ? "#9ca3af" : "#4f46e5" }}
                      />{" "}
                      {testsCount}{" "}
                      {testsCount === 1
                        ? t("overallReadiness.test")
                        : t("overallReadiness.tests")}
                    </span>
                    <span
                      style={
                        chipStyle ?? {
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          padding: "6px 12px",
                          borderRadius: "999px",
                          fontSize: "13px",
                          fontWeight: "600",
                          color: "#047857",
                          background: "rgba(236, 253, 245, 0.98)",
                          border: "1px solid rgba(16, 185, 129, 0.45)",
                          boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                        }
                      }
                    >
                      <FaShieldAlt
                        size={12}
                        style={{ color: isEmpty ? "#9ca3af" : "#059669" }}
                      />{" "}
                      {(testStatsByProfileType[pt]?.blocked ?? 0)} {t("overallReadiness.blocked")}
                    </span>
                    <span
                      style={
                        chipStyle ?? {
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          padding: "6px 12px",
                          borderRadius: "999px",
                          fontSize: "13px",
                          fontWeight: "600",
                          color: "#b45309",
                          background: "rgba(255, 247, 237, 0.98)",
                          border: "1px solid rgba(234, 88, 12, 0.4)",
                          boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                        }
                      }
                    >
                      <FaBug size={12} style={{ color: isEmpty ? "#9ca3af" : "#c2410c" }} />{" "}
                      {(testStatsByProfileType[pt]?.failed_to_block ?? 0)}{" "}
                      {t("overallReadiness.failedToBlock")}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export default OverallReadinessSection;
